const STRIPE_JS_URL = 'https://js.stripe.com/v3/';
let stripeScriptPromise;
let mountedCheckout = null;
let mountedModal = null;
let mountRevision = 0;
let activeModal = null;
let activeWrapper = null;
let modalOpener = null;
let checkoutFlowActive = false;
let checkoutRequestPending = false;
let bodyLockState = null;
let paymentReconciliation = null;
let confirmedReloadAfterClose = null;
let navigationRevision = 0;
let checkoutRequestGeneration = 0;
const reconciliationDelays = [0, 2000, 5000, 10000, 20000];
let activeNajaInstance = null;
let checkoutRequestContext = null;
let csrfRecoveryRetryContext = null;
const registeredNajaInstances = new WeakSet();

function loadStripeJs(documentRef = document) {
    if (typeof globalThis.Stripe === 'function') {
        return Promise.resolve(globalThis.Stripe);
    }

    if (!stripeScriptPromise) {
        stripeScriptPromise = new Promise((resolve, reject) => {
            const script = documentRef.createElement('script');
            script.src = STRIPE_JS_URL;
            script.async = true;
            script.onload = () => {
                if (typeof globalThis.Stripe === 'function') {
                    resolve(globalThis.Stripe);
                } else {
                    stripeScriptPromise = null;
                    reject(new Error('Stripe.js did not expose its client API.'));
                }
            };
            script.onerror = () => {
                stripeScriptPromise = null;
                reject(new Error('Stripe.js could not be loaded.'));
            };
            documentRef.head.append(script);
        });
    }

    return stripeScriptPromise;
}

function getModal(wrapper, documentRef = document) {
    return wrapper?.querySelector('[data-stripe-checkout-modal]')
        ?? documentRef.querySelector('[data-stripe-checkout-modal]')
        ?? null;
}

function getMount(modal) {
    return modal?.querySelector('[data-stripe-checkout-mount]') ?? null;
}

function getStatus(modal) {
    return modal?.querySelector('[data-stripe-checkout-status]') ?? null;
}

function getLoading(modal) {
    return modal?.querySelector('[data-stripe-checkout-loading]') ?? null;
}

function getPaymentArea(wrapper, documentRef = document) {
    return wrapper?.closest?.('[data-stripe-payment-area]')
        ?? documentRef.querySelector('[data-stripe-payment-area]')
        ?? null;
}

function setPaymentState(wrapper, state, documentRef = document) {
    const area = getPaymentArea(wrapper, documentRef);
    const status = area?.querySelector('[data-stripe-submission-status]');
    const retry = area?.querySelector('[data-stripe-reconcile-retry]');
    if (!area || !status) {
        return;
    }

    delete area.dataset.stripeSubmissionPending;
    delete area.dataset.stripeSessionOpen;
    delete area.dataset.stripePaymentConfirmed;
    if (state === 'pending') {
        area.dataset.stripeSubmissionPending = 'true';
        status.textContent = wrapper?.dataset.stripePendingMessage ?? '';
        status.hidden = false;
        if (retry) retry.hidden = true;
    } else if (state === 'open') {
        area.dataset.stripeSessionOpen = 'true';
        status.textContent = wrapper?.dataset.stripeOpenMessage ?? '';
        status.hidden = false;
        if (retry) retry.hidden = true;
        setTriggerDisabled(wrapper, false);
    } else if (state === 'unpaid') {
        status.textContent = wrapper?.dataset.stripeUnpaidMessage ?? '';
        status.hidden = false;
        if (retry) retry.hidden = true;
        setTriggerDisabled(wrapper, false);
    } else if (state === 'paid') {
        area.dataset.stripeSubmissionPending = 'true';
        area.dataset.stripePaymentConfirmed = 'true';
        status.textContent = wrapper?.dataset.stripePaidMessage ?? getModal(wrapper, documentRef)?.dataset.paidMessage ?? '';
        status.hidden = false;
        if (retry) retry.hidden = true;
    } else if (state === 'unavailable') {
        area.dataset.stripeSubmissionPending = 'true';
        status.textContent = wrapper?.dataset.stripeUnavailableMessage ?? '';
        status.hidden = false;
        if (retry) retry.hidden = false;
    } else {
        status.textContent = '';
        status.hidden = true;
        if (retry) retry.hidden = true;
        setTriggerDisabled(wrapper, false);
    }
}

function setStatus(modal, message, {confirmed = false} = {}) {
    const status = getStatus(modal);
    if (status) {
        status.textContent = message;
        if (confirmed) status.classList.add('is-confirmed');
        else status.classList.remove('is-confirmed');
    }
}

function setModalSettlementResult(modal, state) {
    if (!modal) return;
    const paid = state === 'paid';
    modal.classList.remove('is-settlement-pending', 'is-payment-confirmed');
    modal.classList.add(paid ? 'is-payment-confirmed' : 'is-settlement-pending');
    const mount = getMount(modal);
    if (mount) mount.hidden = true;
    const title = modal.querySelector('[data-stripe-checkout-title]');
    if (title) title.textContent = paid ? (modal.dataset.paidTitle ?? '') : (modal.dataset.pendingTitle ?? '');
    setStatus(modal, paid ? (modal.dataset.paidMessage ?? '') : (modal.dataset.completeMessage ?? ''), {confirmed: paid});
}

function resetModalSettlementResult(modal) {
    if (!modal) return;
    modal.classList.remove('is-settlement-pending', 'is-payment-confirmed');
    const mount = getMount(modal);
    if (mount) mount.hidden = false;
    const title = modal.querySelector('[data-stripe-checkout-title]');
    if (title) title.textContent = modal.dataset.titleMessage ?? '';
    setStatus(modal, '');
}

function setLoading(modal, loading, {showMount = false} = {}) {
    if (!modal) {
        return;
    }

    const loadingElement = getLoading(modal);
    const loadingText = modal.querySelector('[data-stripe-checkout-loading-text]');
    const mount = getMount(modal);
    if (loading && loadingText) {
        loadingText.textContent = modal.dataset.loadingMessage ?? '';
    }
    if (loadingElement) {
        loadingElement.hidden = !loading;
    }
    if (mount) {
        mount.hidden = loading || !showMount;
    }
    modal.setAttribute('aria-busy', String(loading));
}

function lockBody(documentRef) {
    if (bodyLockState) {
        return;
    }

    const html = documentRef.documentElement;
    const body = documentRef.body;
    bodyLockState = {
        htmlHadLock: html.classList.contains('stripe-checkout-modal-open'),
        bodyHadLock: body.classList.contains('stripe-checkout-modal-open'),
    };
    html.classList.add('stripe-checkout-modal-open');
    body.classList.add('stripe-checkout-modal-open');
}

function unlockBody(documentRef) {
    if (!bodyLockState) {
        return;
    }

    if (!bodyLockState.htmlHadLock) {
        documentRef.documentElement.classList.remove('stripe-checkout-modal-open');
    }
    if (!bodyLockState.bodyHadLock) {
        documentRef.body.classList.remove('stripe-checkout-modal-open');
    }
    bodyLockState = null;
}

function setTriggerDisabled(wrapper, disabled) {
    const trigger = wrapper?.querySelector('[data-stripe-checkout-trigger]');
    if (trigger) {
        trigger.disabled = disabled;
        trigger.setAttribute('aria-busy', String(disabled));
    }
}

function openModal(wrapper, documentRef = document) {
    const modal = getModal(wrapper, documentRef);
    if (!wrapper || !modal) {
        return false;
    }

    const trigger = wrapper.querySelector('[data-stripe-checkout-trigger]');
    if (activeModal === modal && !modal.hidden) {
        activeWrapper = wrapper;
        checkoutFlowActive = true;
        return true;
    }

    activeModal = modal;
    activeWrapper = wrapper;
    checkoutFlowActive = true;
    resetModalSettlementResult(modal);
    modalOpener = trigger ?? documentRef.activeElement;
    modal.dataset.stripeCheckoutOrderId = trigger?.dataset.stripeCheckoutOrderId ?? modal.dataset.orderId ?? '';
    if (modal.parentElement !== documentRef.body) {
        documentRef.body.append(modal);
    }
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    lockBody(documentRef);
    modal.querySelector('[data-stripe-checkout-close]')?.focus({preventScroll: true});
    return true;
}

function findReturnFocusTarget(documentRef, modal) {
    const orderId = modal?.dataset.stripeCheckoutOrderId;
    if (orderId) {
        const triggers = documentRef.querySelectorAll('[data-stripe-checkout-trigger]');
        for (const trigger of triggers) {
            if (trigger.dataset.stripeCheckoutOrderId === orderId && trigger.isConnected) {
                const area = getPaymentArea(trigger.closest?.('[data-stripe-checkout]'), documentRef);
                if (area?.dataset.stripeSubmissionPending === 'true') {
                    return area.querySelector('[data-stripe-submission-status]') ?? trigger;
                }
                return trigger;
            }
        }
    }
    if (modalOpener?.isConnected) {
        return modalOpener;
    }
    return documentRef.querySelector('.invoice-order-heading .title');
}

function destroyMountedCheckout() {
    mountRevision += 1;
    mountedCheckout?.destroy();
    mountedCheckout = null;
    const mount = getMount(mountedModal);
    mount?.replaceChildren();
    mountedModal = null;
}

function closeModal(documentRef = document, {restoreFocus = true} = {}) {
    const modal = activeModal;
    if (!modal) {
        checkoutFlowActive = false;
        return;
    }

    checkoutFlowActive = false;
    destroyMountedCheckout();
    setTriggerDisabled(activeWrapper, false);
    setLoading(modal, false);
    setStatus(modal, '');
    modal.classList.remove('is-open');
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    unlockBody(documentRef);
    activeModal = null;
    activeWrapper = null;
    const returnTarget = restoreFocus ? findReturnFocusTarget(documentRef, modal) : null;
    modalOpener = null;
    returnTarget?.focus({preventScroll: true});
    const deferredReload = confirmedReloadAfterClose;
    confirmedReloadAfterClose = null;
    if (restoreFocus && deferredReload && currentPageMatchesReconciliation(deferredReload)) {
        globalThis.window?.location?.reload?.();
    }
}

function onModalClick(event, documentRef) {
    const modal = activeModal;
    if (!modal) {
        return;
    }
    const closeButton = event.target?.closest?.('[data-stripe-checkout-close]');
    if (event.target === modal || (closeButton && modal.contains(closeButton))) {
        event.preventDefault();
        closeModal(documentRef);
    }
}

function onModalKeydown(event, documentRef) {
    const modal = activeModal;
    if (!modal) {
        return;
    }

    if (event.key === 'Escape') {
        event.preventDefault();
        closeModal(documentRef);
        return;
    }
    if (event.key !== 'Tab') {
        return;
    }

    const focusable = Array.from(modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])',
    )).filter(element => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
    if (focusable.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
    }

    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && (documentRef.activeElement === first || !modal.contains(documentRef.activeElement))) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && (documentRef.activeElement === last || !modal.contains(documentRef.activeElement))) {
        event.preventDefault();
        first.focus();
    }
}

function onModalFocusIn(event) {
    if (activeModal && !activeModal.contains(event.target)) {
        activeModal.querySelector('[data-stripe-checkout-close]')?.focus({preventScroll: true});
    }
}

function showStripeError(message, documentRef = document) {
    const wrapper = documentRef.querySelector('[data-stripe-checkout]');
    if (!wrapper || !checkoutFlowActive) {
        return;
    }

    const modal = getModal(wrapper, documentRef);
    destroyMountedCheckout();
    openModal(wrapper, documentRef);
    setTriggerDisabled(wrapper, false);
    setLoading(modal, false);
    setStatus(modal, typeof message === 'string' && message !== ''
        ? message
        : modal?.dataset.errorMessage ?? '');
}

function updateStripeCsrfToken(payload, wrapper, documentRef = document) {
    const currentToken = wrapper?.querySelector('input[name="stripe_csrf"]');
    if (typeof payload?.stripeCsrfToken === 'string' && payload.stripeCsrfToken !== '' && currentToken) {
        currentToken.value = payload.stripeCsrfToken;
    }
    const snippetHtml = payload?.snippets?.['snippet--paymentArea'];
    if (typeof snippetHtml !== 'string') {
        return;
    }

    const template = documentRef.createElement('template');
    template.innerHTML = snippetHtml;
    const refreshedToken = template.content.querySelector('input[name="stripe_csrf"]')?.value;
    if (typeof refreshedToken === 'string' && refreshedToken !== '' && currentToken) {
        currentToken.value = refreshedToken;
    }
}

function stripeRequestType(request) {
    if (typeof request?.url !== 'string' || request.url === '') {
        return null;
    }

    try {
        const action = new URL(request.url, globalThis.window?.location?.href ?? 'http://localhost').searchParams.get('do');
        return action === 'createStripeCheckout' || action === 'reconcileStripePayment' ? action : null;
    } catch {
        return null;
    }
}

function canHandleCheckoutError(context, wrapper, documentRef = document) {
    return Boolean(checkoutFlowActive
        && context
        && wrapper
        && checkoutRequestContext === null
        && context.generation === checkoutRequestGeneration
        && context.navigationRevision === navigationRevision
        && context.pageUrl === (globalThis.window?.location?.href ?? '')
        && context.orderId === wrapper.querySelector('[data-stripe-checkout-trigger]')?.dataset.stripeCheckoutOrderId
        && activeModal !== null
        && activeModal === getModal(wrapper, documentRef)
        && !activeModal.hidden);
}

function onCompletedOrderReload(wrapper, documentRef) {
    const context = checkoutRequestContext;
    checkoutRequestContext = null;
    if (!context || !currentPageMatchesReconciliation(context)) {
        return;
    }

    const modal = getModal(wrapper, documentRef);
    if (modal && activeModal === modal && !modal.hidden) {
        confirmedReloadAfterClose = context;
        setPaymentState(wrapper, 'paid', documentRef);
        if (!modal.dataset.paidMessage && wrapper?.dataset.stripePaidMessage) {
            modal.dataset.paidMessage = wrapper.dataset.stripePaidMessage;
        }
        setModalSettlementResult(modal, 'paid');
        getStatus(modal)?.focus({preventScroll: true});
        return;
    }
    globalThis.window?.location?.reload?.();
}

function stopPaymentReconciliation() {
    if (paymentReconciliation && paymentReconciliation.timer !== null) {
        globalThis.clearTimeout(paymentReconciliation.timer);
    }
    paymentReconciliation = null;
}

function currentPageMatchesReconciliation(state) {
    return navigationRevision === state.navigationRevision
        && globalThis.window?.location?.href === state.pageUrl;
}

function hasInFlightSettlementForModal() {
    const orderId = activeModal?.dataset.stripeCheckoutOrderId;
    return Boolean(orderId && (
        paymentReconciliation?.orderId === orderId
        || checkoutRequestContext?.orderId === orderId
    ));
}

function hasMountedCheckoutForInFlightSettlement() {
    return Boolean(activeModal && !activeModal.hidden
        && mountedCheckout && mountedModal === activeModal
        && hasInFlightSettlementForModal());
}

function finishReconciliation(state, outcome, documentRef) {
    if (paymentReconciliation !== state) {
        return;
    }
    if (state.timer !== null) {
        globalThis.clearTimeout(state.timer);
        state.timer = null;
    }

    const wrapper = documentRef.querySelector('[data-stripe-checkout]');
    if (outcome === 'paid' || outcome === 'closed') {
        paymentReconciliation = null;
        const modal = getModal(wrapper, documentRef);
        if (outcome === 'paid' && modal && activeModal === modal && !modal.hidden) {
            confirmedReloadAfterClose = state;
            setPaymentState(wrapper, 'paid', documentRef);
            if (mountedCheckout && mountedModal === modal) {
                // Keep Stripe's native confirmation/receipt mounted until the user closes it.
                setStatus(modal, '');
                modal.querySelector('[data-stripe-checkout-close]')?.focus({preventScroll: true});
            } else {
                if (!modal.dataset.paidMessage && wrapper?.dataset.stripePaidMessage) {
                    modal.dataset.paidMessage = wrapper.dataset.stripePaidMessage;
                }
                setModalSettlementResult(modal, 'paid');
                getStatus(modal)?.focus({preventScroll: true});
            }
            return;
        }
        setPaymentState(wrapper, 'clear', documentRef);
        if (currentPageMatchesReconciliation(state)) {
            globalThis.window?.location?.reload?.();
        }
        return;
    }
    if (outcome === 'pending') {
        state.awaitingSettlement = true;
        state.serverPending = true;
        setPaymentState(wrapper, 'pending', documentRef);
    }
    if (outcome === 'open' && state.awaitingSettlement) {
        if (state.attempt < reconciliationDelays.length) {
            const delay = reconciliationDelays[state.attempt];
            state.timer = globalThis.setTimeout(() => void queryPaymentState(state, documentRef), delay);
        } else {
            state.exhausted = true;
            setPaymentState(wrapper, 'unavailable', documentRef);
        }
        return;
    }
    if (outcome === 'open' || outcome === 'unpaid') {
        paymentReconciliation = null;
        setPaymentState(wrapper, state.awaitingSettlement ? outcome : 'clear', documentRef);
        const modal = getModal(wrapper, documentRef);
        if (state.awaitingSettlement && modal && activeModal === modal && !modal.hidden) {
            setStatus(modal, modal.dataset[`${outcome}Message`] ?? modal.dataset.completeMessage ?? '');
            getStatus(modal)?.focus({preventScroll: true});
        }
        return;
    }

    if (state.attempt < reconciliationDelays.length) {
        const delay = reconciliationDelays[state.attempt];
        state.timer = globalThis.setTimeout(() => void queryPaymentState(state, documentRef), delay);
        return;
    }

    state.exhausted = true;
    if (state.awaitingSettlement) setPaymentState(wrapper, 'unavailable', documentRef);
}

async function queryPaymentState(state, documentRef) {
    if (paymentReconciliation !== state || !currentPageMatchesReconciliation(state)) {
        stopPaymentReconciliation();
        return;
    }

    state.timer = null;
    state.attempt += 1;
    const wrapper = documentRef.querySelector('[data-stripe-checkout]');
    const csrf = wrapper?.querySelector('input[name="stripe_csrf"]')?.value;
    if (!wrapper || !csrf || typeof state.naja?.makeRequest !== 'function') {
        finishReconciliation(state, 'unavailable', documentRef);
        return;
    }

    try {
        const payload = await state.naja.makeRequest('POST', state.url, {stripe_csrf: csrf}, {
            history: false,
            abort: false,
            unique: false,
        });
        if (paymentReconciliation !== state) return;
        updateStripeCsrfToken(payload, wrapper, documentRef);
        finishReconciliation(state, payload?.stripePaymentState ?? 'unavailable', documentRef);
    } catch (error) {
        if (paymentReconciliation !== state) return;
        const response = error?.response;
        if (typeof response?.clone === 'function') {
            try {
                updateStripeCsrfToken(await response.clone().json(), wrapper, documentRef);
            } catch {
                // Keep the latest usable token and retry within the bounded schedule.
            }
        }
        finishReconciliation(state, 'unavailable', documentRef);
    }
}

function startPaymentReconciliation(naja, wrapper, documentRef, {awaitingSettlement = false} = {}) {
    const orderId = wrapper?.dataset.stripeCheckoutOrderId
        || wrapper?.querySelector('[data-stripe-checkout-trigger]')?.dataset.stripeCheckoutOrderId;
    if (!orderId) {
        return;
    }
    if (paymentReconciliation?.orderId === orderId) {
        paymentReconciliation.awaitingSettlement ||= awaitingSettlement;
        if (awaitingSettlement) {
            setPaymentState(wrapper, 'pending', documentRef);
            if (paymentReconciliation.exhausted) {
                paymentReconciliation.attempt = 0;
                paymentReconciliation.exhausted = false;
                void queryPaymentState(paymentReconciliation, documentRef);
            }
        }
        return;
    }
    stopPaymentReconciliation();

    const url = wrapper?.dataset.stripeReconcileUrl;
    if (!url) {
        if (awaitingSettlement) setPaymentState(wrapper, 'unavailable', documentRef);
        return;
    }

    const state = {
        orderId,
        url,
        naja,
        pageUrl: globalThis.window?.location?.href ?? '',
        navigationRevision,
        attempt: 0,
        awaitingSettlement,
        timer: null,
        exhausted: false,
    };
    paymentReconciliation = state;
    if (awaitingSettlement) setPaymentState(wrapper, 'pending', documentRef);
    void queryPaymentState(state, documentRef);
}

async function mountEmbeddedCheckout(payload, documentRef = document) {
    const wrapper = documentRef.querySelector('[data-stripe-checkout]');
    const modal = getModal(wrapper, documentRef);
    const mount = getMount(modal);
    if (!wrapper || !modal || !mount || typeof payload?.clientSecret !== 'string' || payload.clientSecret === ''
        || typeof payload.publishableKey !== 'string' || payload.publishableKey === '') {
        return;
    }

    if (!openModal(wrapper, documentRef)) {
        return;
    }
    const revision = ++mountRevision;
    mountedCheckout?.destroy();
    mountedCheckout = null;
    mountedModal = modal;
    mount.replaceChildren();
    setLoading(modal, true);

    try {
        const Stripe = await loadStripeJs(documentRef);
        if (revision !== mountRevision || !mount.isConnected || activeModal !== modal || modal.hidden) {
            return;
        }

        let submitted = false;
        let completed = false;
        const isCurrentCheckout = () => revision === mountRevision && activeModal === modal && !modal.hidden;
        const checkout = await Stripe(payload.publishableKey).initEmbeddedCheckout({
            clientSecret: payload.clientSecret,
            onAnalyticsEvent: event => {
                if (!isCurrentCheckout()) return;
                if (event.eventType === 'checkoutSubmitted') {
                    submitted = true;
                    startPaymentReconciliation(activeNajaInstance, documentRef.querySelector('[data-stripe-checkout]') ?? wrapper, documentRef, {awaitingSettlement: true});
                } else if (event.eventType === 'checkoutSubmitFailed' && submitted && !completed
                    && !paymentReconciliation?.serverPending && !confirmedReloadAfterClose) {
                    submitted = false;
                    stopPaymentReconciliation();
                    setPaymentState(documentRef.querySelector('[data-stripe-checkout]') ?? wrapper, 'clear', documentRef);
                    setStatus(modal, '');
                }
            },
            onComplete: () => {
                if (!isCurrentCheckout()) {
                    return;
                }
                completed = true;
                setLoading(modal, false, {showMount: true});
                setStatus(modal, modal.dataset.completeMessage ?? '');
                getStatus(modal)?.focus({preventScroll: true});
                startPaymentReconciliation(activeNajaInstance, wrapper, documentRef, {awaitingSettlement: true});
            },
        });
        if (revision !== mountRevision || !mount.isConnected || activeModal !== modal || modal.hidden) {
            checkout.destroy();
            return;
        }

        mountedCheckout = checkout;
        checkout.mount(mount);
        setLoading(modal, false, {showMount: true});
        setStatus(modal, '');
    } catch {
        if (revision !== mountRevision || activeModal !== modal || modal.hidden) {
            return;
        }

        setTriggerDisabled(wrapper, false);
        setLoading(modal, false);
        setStatus(modal, modal.dataset.errorMessage ?? '');
        console.error('Stripe embedded checkout could not be mounted.');
    }
}

function registerStripeCheckout(naja, documentRef = globalThis.document) {
    if (registeredNajaInstances.has(naja)) {
        return;
    }
    registeredNajaInstances.add(naja);
    activeNajaInstance = naja;

    if (documentRef) {
        documentRef.addEventListener('submit', event => {
            const form = event.target?.closest?.('form[data-stripe-checkout-form]');
            if (!form) {
                return;
            }
            const wrapper = form.closest('[data-stripe-checkout]');
            if (!openModal(wrapper, documentRef)) {
                event.preventDefault();
                return;
            }
            if (checkoutRequestPending) {
                event.preventDefault();
                setTriggerDisabled(wrapper, true);
                const modal = getModal(wrapper, documentRef);
                setLoading(modal, true);
                return;
            }

            checkoutRequestPending = true;
            const generation = ++checkoutRequestGeneration;
            const orderId = wrapper?.querySelector('[data-stripe-checkout-trigger]')?.dataset.stripeCheckoutOrderId ?? '';
            const pageUrl = globalThis.window?.location?.href ?? '';
            const retryContext = csrfRecoveryRetryContext;
            const isCsrfRecoveryRetry = retryContext !== null
                && retryContext.orderId === orderId
                && retryContext.pageUrl === pageUrl
                && retryContext.navigationRevision === navigationRevision;
            csrfRecoveryRetryContext = null;
            checkoutRequestContext = isCsrfRecoveryRetry
                ? {...retryContext, generation}
                : {orderId, pageUrl, navigationRevision, generation, csrfRecoveryAttempted: false};
            setTriggerDisabled(wrapper, true);
            const modal = getModal(wrapper, documentRef);
            setLoading(modal, true);
        }, true);

        documentRef.addEventListener('click', event => onModalClick(event, documentRef));
        documentRef.addEventListener('click', event => {
            const retry = event.target?.closest?.('[data-stripe-reconcile-retry]');
            if (!retry || !paymentReconciliation?.exhausted) return;
            event.preventDefault();
            paymentReconciliation.attempt = 0;
            paymentReconciliation.exhausted = false;
            const wrapper = documentRef.querySelector('[data-stripe-checkout]');
            setPaymentState(wrapper, 'pending', documentRef);
            void queryPaymentState(paymentReconciliation, documentRef);
        });
        documentRef.addEventListener('keydown', event => onModalKeydown(event, documentRef));
        documentRef.addEventListener('focusin', onModalFocusIn);
    }

    const windowRef = documentRef?.defaultView ?? globalThis.window;
    windowRef?.addEventListener?.('pagehide', () => {
        navigationRevision += 1;
        checkoutRequestContext = null;
        csrfRecoveryRetryContext = null;
        stopPaymentReconciliation();
        confirmedReloadAfterClose = null;
        setPaymentState(documentRef.querySelector('[data-stripe-checkout]'), 'clear', documentRef);
        closeModal(documentRef, {restoreFocus: false});
    });
    windowRef?.addEventListener?.('popstate', () => {
        navigationRevision += 1;
        checkoutRequestContext = null;
        csrfRecoveryRetryContext = null;
        stopPaymentReconciliation();
        confirmedReloadAfterClose = null;
        setPaymentState(documentRef.querySelector('[data-stripe-checkout]'), 'clear', documentRef);
        closeModal(documentRef, {restoreFocus: false});
    });

    naja.addEventListener('success', event => {
        const payload = event.detail.payload ?? {};
        const wrapper = documentRef?.querySelector('[data-stripe-checkout]');
        const requestType = stripeRequestType(event.detail.request);
        if (requestType === 'createStripeCheckout' || requestType === null) {
            checkoutRequestPending = false;
            csrfRecoveryRetryContext = null;
        }
        if (requestType === 'reconcileStripePayment') {
            return;
        }
        if (payload.stripePaymentState === 'paid') {
            updateStripeCsrfToken(payload, wrapper, documentRef);
            onCompletedOrderReload(wrapper, documentRef);
            return;
        }
        if (payload.stripePaymentState === 'pending') {
            checkoutRequestContext = null;
            updateStripeCsrfToken(payload, wrapper, documentRef);
            setPaymentState(wrapper, 'pending', documentRef);
            setModalSettlementResult(getModal(wrapper, documentRef), 'pending');
            getStatus(getModal(wrapper, documentRef))?.focus({preventScroll: true});
            if (wrapper) startPaymentReconciliation(naja, wrapper, documentRef, {awaitingSettlement: true});
            return;
        }
        if (typeof payload.stripeError === 'string' && payload.stripeError !== '') {
            checkoutRequestContext = null;
            updateStripeCsrfToken(payload, wrapper, documentRef);
            showStripeError(payload.stripeError, documentRef);
            return;
        }

        if (payload.stripe) {
            checkoutRequestContext = null;
            updateStripeCsrfToken(payload, wrapper, documentRef);
            if (checkoutFlowActive) {
                void mountEmbeddedCheckout(payload.stripe, documentRef);
            }
        }
    });

    naja.addEventListener('error', event => {
        if (stripeRequestType(event.detail.request) !== 'createStripeCheckout') {
            return;
        }

        checkoutRequestPending = false;
        const failedRequestContext = checkoutRequestContext;
        checkoutRequestContext = null;
        const wrapper = documentRef?.querySelector('[data-stripe-checkout]');
        if (!wrapper) {
            return;
        }

        const fallbackMessage = getModal(wrapper, documentRef)?.dataset.errorMessage ?? '';
        const response = event.detail.response;
        if (typeof response?.clone === 'function') {
            void response.clone().json()
                .then(payload => {
                    const currentWrapper = documentRef.querySelector('[data-stripe-checkout]');
                    if (!canHandleCheckoutError(failedRequestContext, currentWrapper, documentRef)) {
                        return;
                    }
                    updateStripeCsrfToken(payload, currentWrapper, documentRef);
                    const freshToken = payload?.stripeCsrfToken;
                    if (response.status === 403
                        && payload?.stripeCsrfRejected === true
                        && typeof freshToken === 'string'
                        && freshToken !== ''
                        && failedRequestContext.csrfRecoveryAttempted !== true) {
                        const form = currentWrapper.querySelector('form[data-stripe-checkout-form]');
                        const trigger = currentWrapper.querySelector('[data-stripe-checkout-trigger]');
                        if (form && trigger && typeof form.requestSubmit === 'function') {
                            csrfRecoveryRetryContext = {...failedRequestContext, csrfRecoveryAttempted: true};
                            setTriggerDisabled(currentWrapper, false);
                            try {
                                form.requestSubmit(trigger);
                                return;
                            } catch {
                                csrfRecoveryRetryContext = null;
                            }
                        }
                    }
                    showStripeError(payload?.stripeError ?? fallbackMessage, documentRef);
                })
                .catch(() => {
                    const currentWrapper = documentRef.querySelector('[data-stripe-checkout]');
                    if (canHandleCheckoutError(failedRequestContext, currentWrapper, documentRef)) {
                        showStripeError(fallbackMessage, documentRef);
                    }
                });
        } else {
            showStripeError(fallbackMessage, documentRef);
        }
    });

    naja.addEventListener('abort', event => {
        if (stripeRequestType(event.detail.request) !== 'createStripeCheckout') {
            return;
        }

        checkoutRequestPending = false;
        checkoutRequestContext = null;
        const wrapper = documentRef?.querySelector('[data-stripe-checkout]');
        if (!wrapper) {
            return;
        }

        setTriggerDisabled(wrapper, false);
        const modal = getModal(wrapper, documentRef);
        setLoading(modal, false);
        setStatus(modal, modal?.hidden ? '' : modal?.dataset.errorMessage ?? '');
    });

    naja.snippetHandler.addEventListener('beforeUpdate', event => {
        if (event.detail.snippet?.id !== 'snippet--paymentArea') {
            return;
        }

        if (hasMountedCheckoutForInFlightSettlement()) {
            return;
        }

        destroyMountedCheckout();
    });

    naja.snippetHandler.addEventListener('afterUpdate', event => {
        if (event.detail.snippet?.id !== 'snippet--paymentArea') return;
        const currentWrapper = documentRef.querySelector('[data-stripe-checkout]');
        if (paymentReconciliation?.awaitingSettlement && currentWrapper
            && currentWrapper.dataset.stripeCheckoutOrderId === paymentReconciliation.orderId) {
            setPaymentState(currentWrapper, paymentReconciliation.exhausted ? 'unavailable' : 'pending', documentRef);
        }
        if (!activeModal) return;

        const updatedWrapper = documentRef.querySelector('[data-stripe-checkout]');
        if (!updatedWrapper) {
            if (hasInFlightSettlementForModal()) {
                activeWrapper = null;
                return;
            }
            const modal = activeModal;
            closeModal(documentRef);
            modal?.remove();
            return;
        }
        activeWrapper = updatedWrapper;
    });

    const initialWrapper = documentRef?.querySelector('[data-stripe-checkout]');
    if (initialWrapper?.dataset.stripeReconcileOnLoad === 'true') {
        startPaymentReconciliation(naja, initialWrapper, documentRef);
    }
}

export {loadStripeJs, mountEmbeddedCheckout, registerStripeCheckout};
